/**
 * Cloudinary and ImageKit — which serves hotels.staticroot.com since the MPH-618 migration —
 * share the same delivery path and transformation syntax:
 * `<host>/image/upload/<transformations>/<version>/<public id>`.
 *
 * The hotel images arrive in two shapes, and both accept transformations:
 *   https://hotels.staticroot.com/image/upload/v1701333934/<id>.jpg  (delivery path present)
 *   https://hotels.staticroot.com/gh/<id>.jpg                        (delivery path omitted)
 * Without this check they fall through untransformed and the raw, multi-MB image is served.
 */
const IMAGE_DELIVERY_PATH = '/image/upload/';
const HOTEL_IMAGES_HOST = 'hotels.staticroot.com';

export const isImageUrlTransformable = (url: string): boolean =>
  url.includes(IMAGE_DELIVERY_PATH) || url.includes(HOTEL_IMAGES_HOST);

// The transformation keys Cloudinary and ImageKit accept. Matching on the keys rather than on the
// shape of a parameter is what keeps a folder from being read as a transformation: `gh_es` and
// `it_hotels` are asset paths, `w_0.5` and `t_OSE_redes_item_view` are not.
const TRANSFORMATION_KEYS = [
  'a',
  'ar',
  'b',
  'bo',
  'c',
  'co',
  'd',
  'dpr',
  'e',
  'f',
  'fl',
  'g',
  'h',
  'l',
  'o',
  'p',
  'q',
  'r',
  't',
  'u',
  'w',
  'x',
  'y',
  'z',
];
const TRANSFORMATION_PARAMETER = new RegExp(`^(${TRANSFORMATION_KEYS.join('|')})_.+$`);

/** A transformation already in the path: `c_fill,w_660`, `w_0.5` or `t_OSE_redes_item_view`. */
const isTransformationSegment = (segment: string): boolean =>
  segment.split(',').every((parameter) => TRANSFORMATION_PARAMETER.test(parameter));

const IMAGE_FILE = /\.(jpe?g|png|webp|avif|gif)(\?|$)/i;

/**
 * Put `transformation` in the delivery path, whichever shape the url arrives in — replacing the
 * transformations already there, or splicing the delivery path in after the origin for the urls
 * served without one. The version and the public id always survive: they identify the asset.
 *
 * The single place a transformation is applied, for both the results cards and the hotel detail.
 */
export const applyImageTransformation = (url: string, transformation: string): string => {
  if (!url || !isImageUrlTransformable(url)) return url;

  const deliveryPathIndex = url.indexOf(IMAGE_DELIVERY_PATH);

  if (deliveryPathIndex === -1) {
    // Splice a delivery path only into something that is demonstrably an image file. Guessing
    // wrong turns a working url into a 404, where leaving it alone only serves it raw, as today.
    if (!IMAGE_FILE.test(url)) return url;

    return url.replace(/^((?:https?:)?\/\/[^/]+)\//, `$1${IMAGE_DELIVERY_PATH}${transformation}/`);
  }

  const origin = url.slice(0, deliveryPathIndex);
  const assetPath = url.slice(deliveryPathIndex + IMAGE_DELIVERY_PATH.length).split('/');

  // Never drop the last segment — that is the file, however much its name looks like parameters.
  while (assetPath.length > 1 && isTransformationSegment(assetPath[0])) {
    assetPath.shift();
  }

  return `${origin}${IMAGE_DELIVERY_PATH}${transformation}/${assetPath.join('/')}`;
};

/** A url that already carries a fill transformation, whose width can be rescaled in place. */
const hasFillTransformation = (url: string): boolean => url.indexOf('c_fill') >= 0;

const isImageUrlBooking = (url: string): boolean => url.indexOf('bstatic.com') >= 0;

const rescaleFillTransformationByWidth = (url: string, width: number): string => {
  if (!url) return '';

  const urlArray = url.split('/');
  const imageProperties = urlArray.find((x) => x.includes('c_fill'));
  if (imageProperties) {
    const index = urlArray.indexOf(imageProperties);
    const imagePropertiesArray = imageProperties.split(',');
    const widthString = imagePropertiesArray.find((x) => x.includes('w_'));
    const widthValue = parseInt(widthString?.replace('w_', '') || '', 10);
    const imagePropertiesArrayNew = imagePropertiesArray.map((y) => {
      if (y.indexOf('w_') !== -1) {
        return `w_${width}`;
      }
      if (y.indexOf('h_') !== -1) {
        const height = parseInt(y.replace('h_', ''), 10);
        const newheight = Math.floor((width * height) / widthValue);

        return `h_${newheight}`;
      }
      return y;
    });
    return urlArray
      .map((z, i) => {
        if (i === index) {
          return imagePropertiesArrayNew.join(',');
        }
        return z;
      })
      .join('/');
  }

  return url;
};

/**
 * Size of the box the image is painted into, in CSS pixels. The caller owns it — it is the
 * only place that knows the rendered layout — and it drives both the `width`/`height`
 * attributes and the transformation asked of the CDN, so the two cannot drift apart.
 */
export type ImageSize = { width: number; height: number };

const getTransformation = ({ width, height }: ImageSize): string =>
  `c_fill,w_${width},h_${height},f_auto,q_auto:eco,g_auto`;

// Booking serves fixed resolution buckets, so its widths cannot be derived from the box size.
const BOOKING_IMAGE_WIDTH_1X = 280;
const BOOKING_IMAGE_WIDTH_2X = 560;

const optimiseBookingImageUrlByWidth = (url: string, width: number): string => {
  if (!url) return '';
  return url.replace(/\/max[0-9]+\//, `/max${width}/`);
};

export const getGalleryImageSrc = ({ url, width, height }: { url: string | undefined } & ImageSize): string => {
  if (!url) return '';

  if (isImageUrlTransformable(url)) {
    return applyImageTransformation(url, getTransformation({ width, height }));
  } else if (url.includes('hotelbeds')) {
    return `${url.replace(/original\//, 'bigger/')}`;
  } else if (url.includes('bstatic.com')) {
    return `${optimiseBookingImageUrlByWidth(url, BOOKING_IMAGE_WIDTH_1X)}`;
  } else {
    return url;
  }
};

export const getGalleryImageSrcSet = ({ url, width, height }: { url: string | undefined } & ImageSize): string => {
  if (!url) return '';

  if (isImageUrlTransformable(url)) {
    const src1x = getGalleryImageSrc({ url, width, height });
    const src2x = getGalleryImageSrc({ url, width: width * 2, height: height * 2 });

    return `${src1x} 1x, ${src2x} 2x`;
  } else if (url.includes('hotelbeds')) {
    return `${url.replace(/original\//, 'bigger/')} 1x`;
  } else if (url.includes('bstatic.com')) {
    return `${optimiseBookingImageUrlByWidth(url, BOOKING_IMAGE_WIDTH_1X)} 1x, ${optimiseBookingImageUrlByWidth(
      url,
      BOOKING_IMAGE_WIDTH_2X
    )} 2x`;
  } else {
    return `${url} 1x`;
  }
};

export const convertImageUrlByWidthHeight = (url: string, width: number, height: number): string => {
  if (!url) return '';

  if (isImageUrlTransformable(url)) {
    if (hasFillTransformation(url)) {
      return rescaleFillTransformationByWidth(url, width);
    }
    return applyImageTransformation(url, getTransformation({ width, height }));
  }

  if (isImageUrlBooking(url)) {
    return optimiseBookingImageUrlByWidth(url, width);
  }

  return url;
};
